import type React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as THREE from "three";
import { Html, Line } from "@react-three/drei";
import type { Fixation, Saccade } from "../../types/gazeHeatmapTypes";

/**
 * Format time in seconds to a readable string (e.g., "1.5s" or "0:05")
 */
function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Format duration in ms to a human-readable string
 * - < 1000ms: show as "Xms"
 * - >= 1000ms and < 60s: show as "X.Xs"
 * - >= 60s: show as "Xm Xs"
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

interface ScanpathOverlay3DProps {
  /** Array of fixations to render */
  fixations: Fixation[];
  /** Array of saccades to render */
  saccades: Saccade[];
  /** Current fixation (highlighted) */
  currentFixation?: Fixation | null;
  /** Sphere radius (should match the 360 sphere) */
  sphereRadius?: number;
  /** Color for fixation points */
  fixationColor?: string;
  /** Color for saccade lines */
  saccadeColor?: string;
  /** Color for current fixation highlight */
  currentFixationColor?: string;
  /** Base size for fixation spheres */
  fixationBaseSize?: number;
  /** Whether to show fixation numbers */
  showNumbers?: boolean;
  /** Opacity (0-1) */
  opacity?: number;
  /** Scene rotation Y applied to the 360 sphere (to match coordinates) */
  sceneRotationY?: number;
  /** Horizontal U offset (0-1) for video alignment */
  uOffset?: number;
}

/**
 * Convert UV coordinates to 3D position on a sphere
 * The sphere is viewed from inside, so we need to position elements
 * just inside the sphere surface
 *
 * This formula matches Three.js SphereGeometry's UV mapping:
 *   x = -r * cos(u * 2π) * sin(v * π)
 *   y = r * cos(v * π)
 *   z = r * sin(u * 2π) * sin(v * π)
 *
 * The shader does correctedUv = (1-vUv.x, vUv.y) for BackSide rendering,
 * so a heatmap point at UV (u, v) appears at sphere UV (1-u, v).
 * We need to place the scanpath marker at that same 3D position.
 */
function uvToSpherePosition(u: number, v: number, radius: number, uOffset = 0): THREE.Vector3 {
  // Apply horizontal offset (for video alignment) and wrap to [0, 1]
  let adjustedU = u + uOffset;
  if (adjustedU > 1) adjustedU -= 1;
  if (adjustedU < 0) adjustedU += 1;

  // The heatmap at UV (u, v) is displayed at sphere UV (1-u, 1-v) due to:
  // 1. Shader correction for BackSide: correctedUv.x = 1 - vUv.x
  // 2. V convention difference: our v=0 is bottom (nadir), v=1 is top (zenith)
  //    but Three.js sphere has V=0 at top, V=1 at bottom
  const sphereU = 1.0 - adjustedU;
  const sphereV = 1.0 - v; // Flip V to match sphere convention

  // Three.js SphereGeometry parameterization
  const theta = sphereU * 2 * Math.PI; // Azimuth angle (0 to 2π)
  const phi = sphereV * Math.PI; // Polar angle (0 at top to π at bottom)

  // Position slightly inside the sphere surface so it's visible
  const innerRadius = radius * 0.98;

  // Three.js SphereGeometry formula
  const x = -innerRadius * Math.cos(theta) * Math.sin(phi);
  const y = innerRadius * Math.cos(phi);
  const z = innerRadius * Math.sin(theta) * Math.sin(phi);

  return new THREE.Vector3(x, y, z);
}

/**
 * 3D overlay for rendering scanpath on the inside of the 360° sphere
 * Renders fixations as spheres and saccades as lines
 */
const ScanpathOverlay3D: React.FC<ScanpathOverlay3DProps> = ({
  fixations,
  saccades,
  currentFixation,
  sphereRadius = 500,
  fixationColor = "#3b82f6",
  saccadeColor = "#ffffff",
  currentFixationColor = "#ef4444",
  fixationBaseSize = 44, // Base size in pixels for HTML rendering
  showNumbers = true,
  opacity = 1,
  sceneRotationY: _sceneRotationY = 0, // Kept for API compatibility but no longer used
  uOffset = 0, // Horizontal offset for video alignment
}) => {
  const { t } = useTranslation();
  const [hoveredFixationId, setHoveredFixationId] = useState<number | null>(null);

  // Create saccade line data for each segment
  const saccadeLineData = useMemo(() => {
    if (saccades.length === 0) return [];

    const lines: Array<[THREE.Vector3, THREE.Vector3]> = [];

    for (const saccade of saccades) {
      const from = fixations.find((f) => f.id === saccade.fromFixationId);
      const to = fixations.find((f) => f.id === saccade.toFixationId);

      if (!from || !to) continue;

      const startPos = uvToSpherePosition(
        from.centroidUV.u,
        from.centroidUV.v,
        sphereRadius,
        uOffset,
      );
      const endPos = uvToSpherePosition(to.centroidUV.u, to.centroidUV.v, sphereRadius, uOffset);

      lines.push([startPos, endPos]);
    }

    return lines;
  }, [saccades, fixations, sphereRadius, uOffset]);

  return (
    <group>
      {/* Saccade lines - using Line from drei for thick lines */}
      {saccadeLineData.map((points, index) => (
        <Line
          key={index}
          points={points}
          color={saccadeColor}
          lineWidth={3}
          opacity={opacity * 0.9}
          transparent={true}
        />
      ))}

      {/* Fixation markers - rendered as HTML to ensure proper layering */}
      {fixations.map((fixation) => {
        const position = uvToSpherePosition(
          fixation.centroidUV.u,
          fixation.centroidUV.v,
          sphereRadius,
          uOffset,
        );

        const isCurrent = currentFixation?.id === fixation.id;
        const isHovered = hoveredFixationId === fixation.id;
        const color = isCurrent ? currentFixationColor : fixationColor;

        // Scale size by duration
        const durationScale = Math.sqrt(fixation.duration / 100);
        const sizeMultiplier = Math.min(2, Math.max(0.75, durationScale));
        const pixelSize = Math.round(fixationBaseSize * sizeMultiplier);

        return (
          <group key={fixation.id} position={position}>
            {/* All fixation visuals in HTML for proper layering */}
            <Html
              center
              occlude={false}
              zIndexRange={[40, 0]}
              style={{
                pointerEvents: "auto",
                userSelect: "none",
                opacity: opacity,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: `${pixelSize}px`,
                  height: `${pixelSize}px`,
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHoveredFixationId(fixation.id)}
                onMouseLeave={() => setHoveredFixationId(null)}
              >
                {/* Blue circle background (the fixation point) */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    backgroundColor: `${color}cc`, // Semi-transparent
                    border: `3px solid ${color}`,
                    boxShadow:
                      isCurrent || isHovered
                        ? `0 0 0 4px ${
                            isCurrent ? currentFixationColor : fixationColor
                          }40, 0 2px 8px rgba(0,0,0,0.5)`
                        : "0 2px 8px rgba(0,0,0,0.5)",
                    transform: isHovered ? "scale(1.1)" : "scale(1)",
                    transition: "transform 0.15s ease-out",
                  }}
                />
                {/* Number label on top */}
                {showNumbers && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: `${Math.max(12, Math.round(pixelSize * 0.5))}px`,
                      fontWeight: "bold",
                      textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                    }}
                  >
                    {fixation.id + 1}
                  </div>
                )}

                {/* Tooltip on hover */}
                {isHovered && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      backgroundColor: "rgba(0, 0, 0, 0.9)",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                      zIndex: 1000,
                    }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                      {t("statsNew.scanpath.fixation")} #{fixation.id + 1}
                    </div>
                    <div
                      style={{ display: "flex", flexDirection: "column", gap: "2px", opacity: 0.9 }}
                    >
                      <span>
                        ⏱ {t("statsNew.scanpath.startTime")}: {formatTime(fixation.startTime)}
                      </span>
                      <span>
                        ⏳ {t("statsNew.scanpath.duration")}: {formatDuration(fixation.duration)}
                      </span>
                      <span>
                        👁 {t("statsNew.scanpath.points")}: {fixation.pointCount}
                      </span>
                      <span>
                        💪 {t("statsNew.scanpath.strength")}: {Math.round(fixation.strength * 100)}%
                      </span>
                    </div>
                    {/* Tooltip arrow */}
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "6px solid rgba(0, 0, 0, 0.9)",
                      }}
                    />
                  </div>
                )}
              </div>
            </Html>
          </group>
        );
      })}
    </group>
  );
};

export default ScanpathOverlay3D;
